/**
 * LES LIBELLÉS D'ENUM — pour qu'un acte d'État ne s'imprime pas en jargon.
 *
 * ⚠️ MESURÉ SUR LE PREMIER CERTIFICAT RENDU : la colonne « Qualité » portait
 * `personne_physique` et la nature du droit `propriete`, sans accent. Ce sont
 * les valeurs de l'énumération PostgreSQL, justes en base et illisibles sur un
 * papier remis à un citoyen. Un `.replace('_', ' ')` ne suffit pas : il rend
 * « propriete », pas « propriété », et « nue propriete » pour une nue-propriété.
 *
 * ⚠️ CE N'EST PAS UNE RÈGLE MÉTIER, C'EST UNE TRADUCTION. Les règles de ce
 * dépôt vivent en base (`sec.role_permission`, `doc.acte_autorite`) parce
 * qu'elles changent et se contredisent ; l'orthographe de « usufruit » ne
 * change pas, et la mettre en base imposerait une migration pour corriger une
 * coquille.
 *
 * ⚠️ UNE VALEUR INCONNUE EST RENDUE LISIBLE, JAMAIS MASQUÉE NI INVENTÉE. Si un
 * jour l'énumération gagne une valeur sans passer ici, l'acte imprimera
 * « bail a construction » — laid, et parfaitement compréhensible. Rendre une
 * chaîne vide ferait disparaître la nature du droit d'un titre de propriété,
 * et personne ne s'en apercevrait avant qu'un notaire ne le signale.
 * `tests/libelles.test.ts` confronte cette table aux énumérations réelles.
 */

import { LIBELLE_PIECE } from './pieces-identite.js';

const NATURE_DROIT: Record<string, string> = {
  propriete: 'Propriété',
  usufruit: 'Usufruit',
  nue_propriete: 'Nue-propriété',
  bail_emphyteotique: 'Bail emphytéotique',
  occupation_coutumiere: 'Occupation coutumière',
  concession: 'Concession',
  servitude: 'Servitude',
  hypotheque: 'Hypothèque',
  saisie: 'Saisie',
  prenotation: 'Prénotation',
};

const TYPE_TITULAIRE: Record<string, string> = {
  personne_physique: 'Personne physique',
  personne_morale: 'Personne morale',
  etat: 'État',
  collectivite: 'Collectivité',
  indivision: 'Indivision',
  communaute_coutumiere: 'Communauté coutumière',
  inconnu: 'Non déterminé',
};

const TYPE_MUTATION: Record<string, string> = {
  immatriculation: 'Immatriculation',
  vente: 'Vente',
  succession: 'Succession',
  donation: 'Donation',
  echange: 'Échange',
  division: 'Division',
  fusion: 'Fusion',
  rectification: 'Rectification',
  radiation: 'Radiation',
  inscription_surete: 'Inscription de sûreté',
  mainlevee: 'Mainlevée',
};

/**
 * LES PIÈCES QUI ÉTABLISSENT UNE IDENTITÉ EN GUINÉE.
 *
 * ⚠️⚠️ CETTE TABLE ÉTAIT UNE CINQUIÈME COPIE, ET SON COMMENTAIRE ANNONÇAIT UNE
 * GARDE QUI N'EXISTAIT PAS. Il disait « `tests/libelles.test.ts` les
 * confronte » — mesuré le 14/09/2026 : aucun essai ne lisait
 * `TABLES_LIBELLES_FORMULAIRE`, exportée et employée par RIEN. Une règle écrite
 * et jamais branchée, exactement comme la promesse de `gabarits.test.ts` avant
 * qu'on ne l'écrive.
 *
 * Elle suivait de surcroît la MAUVAISE liste — celle de `domaine/formulaires.ts`,
 * qui portait `carte_nationale` et `attestation_identite` que la base refuse.
 * Elle dérive désormais de `partage/pieces-identite.ts`, le seul vocabulaire,
 * et `tests/pieces-identite.test.ts` confronte réellement les quatre.
 */
const PIECE_IDENTITE: Record<string, string> = { ...LIBELLE_PIECE };

/**
 * LE LIEN ENTRE LES PARTIES D'UNE DONATION.
 *
 * ⚠️ IL N'EST PAS DÉCORATIF : il conditionne les droits d'enregistrement et la
 * réserve héréditaire. « Aucun » est une valeur licite et informative, pas un
 * défaut de saisie — et le libellé doit le dire, sinon un lecteur conclut que
 * la case n'a pas été remplie.
 */
const LIEN_PARTIES: Record<string, string> = {
  ascendant_descendant: 'Ascendant à descendant',
  entre_epoux: 'Entre époux',
  collateral: 'Collatéral (frère, sœur, oncle, neveu)',
  autre_famille: 'Autre lien de famille',
  aucun: 'Aucun lien de famille déclaré',
};

/**
 * L'ORIGINE DE LA GÉOMÉTRIE D'UNE PARCELLE — `cad.parcelle.origine`.
 *
 * ⚠️ MESURÉ SUR LE PREMIER ACTE DE DONATION RENDU EN PDF : la ligne « Origine
 * de la géométrie » portait `leve_terrain`, et « Statut de la parcelle »
 * portait `active`. Ce sont les valeurs de l'énumération PostgreSQL — justes en
 * base, illisibles sur un acte remis à un citoyen. Aucun essai ne pouvait le
 * voir : le PDF sortait, la route rendait 201. C'est en REGARDANT la page que
 * le jargon se voit.
 *
 * ⚠️ CETTE DISTINCTION N'EST PAS ARBITRAIRE : `origine` et `statut` SONT des
 * énumérations (6 et 5 valeurs, confrontées par `tests/libelles.test.ts`).
 * `methode`, lui, est du TEXTE LIBRE — mesuré le 14/09/2026 : « quadrillage
 * 25 m (projection 32628) » sur 988 688 parcelles, « GNSS RTK — parcours de
 * recette » sur 2, « gnss_rtk » sur 2. Le faire passer par une table le
 * RÉÉCRIRAIT : ce champ dit ce que le géomètre a saisi, et un acte d'État ne
 * réécrit pas un levé pour le rendre plus joli.
 */
const ORIGINE_PARCELLE: Record<string, string> = {
  leve_terrain: 'Levé de terrain',
  numerisation_plan: 'Numérisation de plan',
  derive_bati: 'Dérivée du bâti',
  declaration: 'Déclaration',
  import: 'Import de données',
  maille_systematique: 'Maillage systématique',
};

const STATUT_PARCELLE: Record<string, string> = {
  projet: 'Projet',
  en_instruction: 'En instruction',
  active: 'Active',
  suspendue: 'Suspendue',
  retiree: 'Retirée',
};

/**
 * SITUATION ET RÉGIME MATRIMONIAUX, ORIGINE DE PROPRIÉTÉ.
 *
 * ⚠️ « INCONNU » SE DIT, IL NE SE MASQUE PAS. Un régime « inconnu » imprimé tel
 * quel prévient l'autorité réceptrice qu'elle doit vérifier avant de recevoir ;
 * une case vide la laisserait croire à un oubli de saisie. Les deux se
 * ressemblent sur le papier, et une seule est vraie.
 */
const SITUATION_MATRIMONIALE: Record<string, string> = {
  celibataire: 'Célibataire',
  marie: 'Marié(e)',
  veuf: 'Veuf / Veuve',
  divorce: 'Divorcé(e)',
  separe: 'Séparé(e)',
};

const REGIME_MATRIMONIAL: Record<string, string> = {
  communaute_des_biens: 'Communauté des biens',
  separation_des_biens: 'Séparation des biens',
  polygamie_declaree: 'Polygamie déclarée',
  coutumier: 'Régime coutumier',
  inconnu: 'Régime non déterminé — à vérifier avant réception',
};

const ORIGINE_PROPRIETE: Record<string, string> = {
  titre_foncier: 'Titre foncier',
  achat: 'Achat',
  heritage: 'Héritage',
  donation_anterieure: 'Donation antérieure',
  partage: 'Partage',
  attribution_administrative: 'Attribution administrative',
  attribution_coutumiere: 'Attribution coutumière',
  occupation_prolongee: 'Occupation prolongée',
  autre: 'Autre origine',
};

/**
 * LE VERDICT D'UN RIVERAIN AU BORNAGE — quatre libellés, DEUX conséquences.
 *
 * ⚠️⚠️ LA DISTINCTION N'EST PAS COSMÉTIQUE : ELLE DÉCIDE DE LA SUITE DU DOSSIER.
 * `releves.ts` porte la doctrine et elle est juste : « absent » et « refus de
 * signer » ne sont PAS des oppositions. Ils disent que le CONTRADICTOIRE N'A
 * PAS EU LIEU, ce qui appelle une RECONVOCATION — pas une instruction de
 * litige. Confondre les deux envoie un dossier en litige là où il fallait
 * reconvoquer le voisin ; l'inverse fait passer une contestation réelle pour un
 * simple défaut de comparution.
 *
 * ⚠️ CETTE TABLE EST PARTAGÉE PAR LE PLAN ET PAR L'ACTE, ET C'EST LE POINT.
 * `plan-cadastral-natif` allait recopier une correspondance dans `plans.ts` ;
 * deux tables du même vocabulaire divergent au premier verdict ajouté — et ce
 * sera un verdict qui décide entre un litige et une reconvocation. Un plan et
 * un acte du même dossier ne peuvent pas compter différemment.
 *
 * ⚠️ ET LE PLAN IMPRIMAIT `refus_de_signer` TEL QUEL sur un document d'État —
 * même défaut que `leve_terrain` et `active` sur mon acte, trouvé en regardant
 * le PDF. Une valeur d'énumération n'est pas un libellé.
 */
const VERDICT_RIVERAIN: Record<string, string> = {
  concordant: 'Concordant',
  opposition: 'Opposition',
  refus_de_signer: 'Refus de signer',
  absent: 'Absent',
};

/**
 * LE STATUT D'UN LEVÉ — `cad.releve.statut`.
 *
 * ⚠️ TROISIÈME FOIS QUE CE MOTIF SE PRÉSENTE DANS CE FICHIER, et c'est
 * pourquoi il y entre au lieu d'être écrit dans la route : `leve_terrain` et
 * `active` se sont imprimés tels quels sur un acte de donation,
 * `refus_de_signer` sur un plan de bornage. Le procès-verbal de bornage porte
 * le statut du levé en tête de page — sans table, il aurait imprimé
 * « en_controle » sur une pièce d'État.
 *
 * ⚠️ « DÉPOSÉ » ET « VALIDÉ » NE SONT PAS DES NUANCES DE PRÉSENTATION : un levé
 * déposé n'a produit AUCUN effet sur le registre, un levé validé a rendu une
 * parcelle opposable. Le libellé le dit, parce que le lecteur d'un PV décide
 * sur cette ligne s'il peut s'en prévaloir.
 */
const STATUT_RELEVE: Record<string, string> = {
  brouillon: 'Brouillon — non déposé',
  depose: 'Déposé — sans effet sur le registre à ce jour',
  en_controle: 'En contrôle',
  valide: 'Validé — la géométrie est opposable',
  rejete: 'Rejeté',
};

/**
 * LA MÉTHODE D'UN LEVÉ — `cad.releve.methode`.
 *
 * ⚠️⚠️ NE PAS CONFONDRE AVEC `cad.parcelle.methode`, ET LA DIFFÉRENCE EST
 * MESURÉE. Le commentaire d'`ORIGINE_PARCELLE` ci-dessus explique pourquoi la
 * méthode d'une PARCELLE ne passe PAS par une table : c'est du TEXTE LIBRE
 * (« quadrillage 25 m (projection 32628) » sur 988 688 parcelles), et un acte
 * d'État ne réécrit pas la saisie d'un géomètre pour la rendre plus jolie.
 *
 * Celle d'un LEVÉ est l'inverse : `cad.releve` porte
 * `CHECK (methode = ANY (ARRAY['gnss_rtk','gnss_statique','station_totale',
 * 'gps_navigation','photo_interpretation']))` — cinq valeurs, closes en base ET
 * dans le `z.enum` de `modules/releves.ts`. C'est une énumération qui ne dit
 * pas son nom, et elle s'imprimait telle quelle : « Méthode employée :
 * gnss_rtk » sur le premier procès-verbal de bornage rendu. Défaut vu en
 * REGARDANT le PDF, comme `leve_terrain`, `active` et `refus_de_signer` avant lui.
 *
 * ⚠️ `tests/libelles.test.ts` confronte cette table au CHECK lui-même, pas à
 * une liste recopiée : une sixième méthode ajoutée en base rougira ici.
 */
const METHODE_RELEVE: Record<string, string> = {
  gnss_rtk: 'GNSS temps réel (RTK)',
  gnss_statique: 'GNSS statique',
  station_totale: 'Station totale',
  gps_navigation: 'GPS de navigation',
  photo_interpretation: 'Photo-interprétation',
};

/**
 * LE STATUT D'UNE MUTATION — `fon.statut_mutation`.
 *
 * ⚠️ IL S'IMPRIMAIT `validee`, SANS ACCENT, sur la fiche d'identification des
 * parties — à la ligne « Statut au registre », c'est-à-dire celle qui dit si
 * l'acte a produit effet. Quatrième occurrence du même motif dans ce dépôt.
 */
const STATUT_MUTATION: Record<string, string> = {
  deposee: 'Déposée',
  instruction: 'En instruction',
  controle_technique: 'Contrôle technique',
  attente_validation: 'En attente de validation',
  validee: 'Validée — appliquée au registre',
  rejetee: 'Rejetée',
  annulee: 'Annulée',
};

/**
 * Les trois suites possibles. `contradictoire_non_abouti` n'est pas un
 * désaccord : c'est une convocation à refaire.
 */
export type CategorieVerdict = 'accord' | 'contestation' | 'contradictoire_non_abouti';

const CATEGORIE_VERDICT: Record<string, CategorieVerdict> = {
  concordant: 'accord',
  opposition: 'contestation',
  refus_de_signer: 'contradictoire_non_abouti',
  absent: 'contradictoire_non_abouti',
};

/**
 * LES BORNES — nature et état, en français.
 *
 * ⚠️ AJOUTÉES LE 17/09/2026 APRÈS AVOIR REGARDÉ LE PREMIER PLAN DE BORNAGE
 * RÉELLEMENT DÉLIVRÉ DU PAYS. Son tableau des coordonnées imprimait
 * « non_materialisee » et « posee » — la valeur brute de l'enum, avec son
 * souligné, sur un document d'État. Aucun essai ne pouvait le voir : la valeur
 * est juste en base, seule sa FORME est fautive. Et la garde anti-jargon
 * existante ne couvrait que le PV et la fiche des parties, pas les plans.
 *
 * ⚠️ ET LE DÉFAUT NE POUVAIT PAS APPARAÎTRE AVANT : `cad.borne` était vide
 * depuis toujours, donc ce chemin n'avait jamais imprimé une seule ligne. Un
 * garde qui ne trouve aucun cas est vert pour la pire des raisons.
 */
const TYPE_BORNE: Record<string, string> = {
  borne_beton: 'Borne en béton',
  borne_pierre: 'Borne en pierre',
  piquet_fer: 'Piquet de fer',
  angle_de_mur: 'Angle de mur',
  angle_de_cloture: 'Angle de clôture',
  arbre_repere: 'Arbre-repère',
  point_peint: 'Point peint',
  /**
   * ⚠️ « NON MATÉRIALISÉE » N'EST PAS UNE ABSENCE DE BORNE : le point est
   * mesuré et inscrit, mais rien ne le matérialise sur le terrain. Le lire
   * « pas de borne » ferait conclure qu'un côté n'est pas borné.
   */
  non_materialisee: 'Non matérialisée',
};

const ETAT_BORNE: Record<string, string> = {
  posee: 'Posée',
  retrouvee: 'Retrouvée',
  detruite: 'Détruite',
  retablie: 'Rétablie',
  deplacee: 'Déplacée',
};

/** Repli : lisible et honnête — on n'invente pas, on nettoie. */
const defaut = (v: string) => {
  const brut = v.replace(/_/g, ' ').trim();
  return brut ? brut.charAt(0).toUpperCase() + brut.slice(1) : '—';
};

export const libelleNature = (v: unknown): string =>
  NATURE_DROIT[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleTypeTitulaire = (v: unknown): string =>
  TYPE_TITULAIRE[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleTypeMutation = (v: unknown): string =>
  TYPE_MUTATION[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libellePieceIdentite = (v: unknown): string =>
  PIECE_IDENTITE[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleLienParties = (v: unknown): string =>
  LIEN_PARTIES[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleOrigineParcelle = (v: unknown): string =>
  ORIGINE_PARCELLE[String(v ?? '')] ?? defaut(String(v ?? ''));

/**
 * L'USAGE D'UNE EMPRISE BÂTIE — vocabulaire d'OpenStreetMap, rendu en français.
 *
 * ⚠️ POURQUOI CETTE TABLE EXISTE. `cad.batiment.usage` portait ces valeurs
 * ANGLAISES telles quelles à l'écran : la fiche d'une parcelle affichait
 * « 475 m² · yes », où « yes » n'est pas un usage mais le mot qu'OSM emploie
 * pour dire « c'est un bâtiment, et je ne sais pas lequel ». Mesuré le
 * 24/09/2026 sur 1 744 535 emprises : 79 valeurs distinctes, dont les 24
 * ci-dessous couvrent 99,99 %.
 *
 * ⚠️ LE REPLI REND LA VALEUR BRUTE, JAMAIS UN LIBELLÉ INVENTÉ. Les 55 valeurs
 * de la queue (0,01 %) s'afficheront en anglais, et c'est voulu : une valeur
 * inconnue qui se déguise en valeur connue est pire qu'une valeur visible.
 */
const USAGE_BATI: Record<string, string> = {
  yes: 'usage non précisé', hut: 'case', house: 'maison',
  construction: 'en construction', residential: 'habitation', school: 'école',
  mosque: 'mosquée', cabin: 'cabane', apartments: 'immeuble d\'habitation',
  shed: 'remise', detached: 'maison individuelle', industrial: 'industriel',
  commercial: 'commerce', hospital: 'hôpital', hangar: 'hangar',
  damaged: 'endommagé', roof: 'toiture seule', ruins: 'ruines', farm: 'ferme',
  warehouse: 'entrepôt', university: 'université',
  allotment_house: 'maison de lotissement', public: 'bâtiment public',
  retail: 'commerce de détail',
};

/**
 * LA SURFACE D'UNE PARCELLE — un seul rendu, pour tout l'écran.
 *
 * ⚠️ TROIS RENDUS COEXISTAIENT LE 24/09/2026, dans le même écran : le panneau
 * de section sortait `931.5` brut, la fiche employait `toFixed` et rendait
 * « 5302 m² » sans séparateur, la table attributaire employait `toLocaleString`
 * et rendait « 5 302,2 m² ». Sur un registre, deux écritures d'une même valeur
 * obligent le lecteur à se demander si ce sont deux choses — et il lui arrive
 * d'avoir raison de se le demander, ce qui est pire.
 */
export function surfaceEnFrancais(v: unknown): string {
  /* ⚠️ `Number(null)` ET `Number('')` VALENT ZÉRO, ET ZÉRO EST FINI. Sans ce
     contrôle, une surface ABSENTE s'affiche « 0 m² » — une affirmation, là où
     il n'y a qu'un trou. Sur un registre foncier, dire qu'une parcelle fait
     zéro mètre carré n'est pas une imprécision, c'est une erreur. Le défaut
     vivait déjà dans le `metres()` de la fiche de parcelle. */
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n >= 10_000
    ? `${(n / 10_000).toLocaleString('fr-FR', { maximumFractionDigits: 3 })} ha`
    : `${n.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m²`;
}

/** Les clés réellement répertoriées — « hangar » se traduit par lui-même, donc
 *  comparer le rendu à la valeur ne dit PAS si elle est connue. */
export const USAGES_BATI_CONNUS: readonly string[] = Object.keys(USAGE_BATI);

export const libelleUsageBati = (v: unknown): string => {
  const brut = String(v ?? '').trim();
  if (!brut) return 'usage inconnu';
  return USAGE_BATI[brut] ?? brut;
};

export const libelleStatutParcelle = (v: unknown): string =>
  STATUT_PARCELLE[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleSituationMatrimoniale = (v: unknown): string =>
  SITUATION_MATRIMONIALE[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleRegimeMatrimonial = (v: unknown): string =>
  REGIME_MATRIMONIAL[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleOriginePropriete = (v: unknown): string =>
  ORIGINE_PROPRIETE[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleVerdictRiverain = (v: unknown): string =>
  VERDICT_RIVERAIN[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleStatutReleve = (v: unknown): string =>
  STATUT_RELEVE[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleMethodeReleve = (v: unknown): string =>
  METHODE_RELEVE[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleStatutMutation = (v: unknown): string =>
  STATUT_MUTATION[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleTypeBorne = (v: unknown): string =>
  TYPE_BORNE[String(v ?? '')] ?? defaut(String(v ?? ''));

export const libelleEtatBorne = (v: unknown): string =>
  ETAT_BORNE[String(v ?? '')] ?? defaut(String(v ?? ''));

/**
 * UN NOMBRE SUR UN ACTE D'ÉTAT FRANÇAIS PORTE UNE VIRGULE, PAS UN POINT.
 *
 * ⚠️ VU SUR LE PREMIER PROCÈS-VERBAL RENDU : « ± 0.15 m ». Le point décimal est
 * la forme anglaise ; il se glisse partout où l'on interpole un `number` dans
 * une chaîne, et aucun essai ne le voit — le PDF sort, la route rend 200.
 * Même famille que « Fri Sep 11 » sur le premier titre délivré.
 */
export function nombreEnFrancais(v: unknown, decimales?: number): string {
  // ⚠️ `Number(null)` VAUT 0, ET `Number('')` AUSSI. Sans ce garde, une
  // précision absente s'imprimerait « ± 0 m » — c'est-à-dire une précision
  // PARFAITE annoncée là où la donnée manque. Défaut trouvé par l'essai qui
  // accompagne cette fonction, avant toute livraison.
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '—';
  const s = decimales === undefined ? String(n) : n.toFixed(decimales);
  return s.replace('.', ',');
}

/**
 * ⚠️ LE REPLI EST `contradictoire_non_abouti`, PAS `accord`. Un verdict inconnu
 * doit appeler une vérification humaine, jamais valider en silence. Le jour où
 * l'énumération gagne une valeur sans passer ici, le dossier sera reconvoqué —
 * gênant, mais réparable — au lieu d'être tenu pour acquis.
 */
export const categorieVerdictRiverain = (v: unknown): CategorieVerdict =>
  CATEGORIE_VERDICT[String(v ?? '')] ?? 'contradictoire_non_abouti';

/** Exposées pour que l'essai puisse les confronter aux énumérations en base. */
export const TABLES_LIBELLES = {
  verdict_riverain: VERDICT_RIVERAIN,
  statut_releve: STATUT_RELEVE,
  statut_mutation: STATUT_MUTATION,
  nature_droit: NATURE_DROIT,
  type_titulaire: TYPE_TITULAIRE,
  type_mutation: TYPE_MUTATION,
  origine_parcelle: ORIGINE_PARCELLE,
  statut_parcelle: STATUT_PARCELLE,
  type_borne: TYPE_BORNE,
  etat_borne: ETAT_BORNE,
} as const;

/** Les tables adossées à des énumérations ZOD, non PostgreSQL : le banc les
 *  confronte à `PIECES_IDENTITE` et au `z.enum` du lien, pas à `pg_enum`. */
/**
 * ⚠️ `methode_releve` N'EST PAS UNE ÉNUMÉRATION POSTGRESQL : c'est un CHECK sur
 * une colonne `text`. Le banc le confronte à la DÉFINITION du CHECK, pas à
 * `pg_enum` — l'y chercher rendrait zéro valeur, donc « toutes couvertes ».
 */
export const TABLES_LIBELLES_CHECK = {
  methode_releve: METHODE_RELEVE,
} as const;

export const TABLES_LIBELLES_FORMULAIRE = {
  piece_identite: PIECE_IDENTITE,
  lien_parties: LIEN_PARTIES,
  situation_matrimoniale: SITUATION_MATRIMONIALE,
  regime_matrimonial: REGIME_MATRIMONIAL,
  origine_propriete: ORIGINE_PROPRIETE,
} as const;

/**
 * UNE DATE SUR UN ACTE D'ÉTAT S'ÉCRIT EN FRANÇAIS, EN TOUTES LETTRES.
 *
 * ⚠️ MESURÉ SUR LE PREMIER TITRE RÉELLEMENT DÉLIVRÉ (GN-TIT-000000002) : le
 * certificat portait « Délivré le Fri Sep 11 ». Cause : `date_emission` arrive
 * de `pg` comme un objet `Date`, et `String(date).slice(0, 10)` rend les dix
 * premiers caractères de sa forme anglaise — « Fri Sep 11 » — au lieu d'une
 * date ISO. Le découpage paraissait juste parce qu'il l'est sur une CHAÎNE ISO,
 * et le typage `any` de la ligne SQL ne pouvait rien en dire.
 *
 * ⚠️ ET UNE DATE ILLISIBLE NE SE VOIT PAS EN ESSAI. Elle s'imprime, le PDF
 * sort, le parcours passe au vert : c'est un lecteur humain qui l'attrape — ici,
 * en regardant la page rendue.
 */
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
              'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

export function dateEnFrancais(v: unknown): string {
  const d = v instanceof Date ? v : new Date(String(v ?? ''));
  // ⚠️ Une date invalide est DITE, jamais imprimée en « Invalid Date » ni
  // remplacée par aujourd'hui : un acte daté du jour de son impression plutôt
  // que de sa délivrance serait un faux involontaire.
  if (Number.isNaN(d.getTime())) return 'date non renseignée';
  return `${d.getUTCDate()} ${MOIS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/**
 * LA MÊME DATE, POUR UNE MACHINE — AAAA-MM-JJ.
 *
 * ⚠️ POURQUOI CE JUMEAU EXISTE. `dateEnFrancais` règle la date IMPRIMÉE ; il
 * restait le cas de la date qu'on RENVOIE (un champ `DATE_ISO` d'un schéma, une
 * clé de formulaire). Faute d'équivalent, chaque appelant réécrivait
 * `String(date).slice(0, 10)` — qui rend « Mon Sep 14 » sur un objet `Date`.
 * Le défaut a été corrigé TROIS FOIS en trois endroits différents parce que le
 * geste juste n'avait pas de nom. Il en a un.
 *
 * ⚠️ ET UNE DATE INVALIDE REND `null`, JAMAIS AUJOURD'HUI. Un formulaire
 * d'immatriculation qui porterait la date de son dépôt à la place de la date de
 * détention affirmerait une possession qui n'a pas eu lieu.
 */
export function dateIso(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  const d = v instanceof Date ? v : new Date(String(v));
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * UN PRIX SUR UN ACTE S'ÉCRIT DEUX FOIS — EN CHIFFRES ET EN TOUTES LETTRES
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ CE N'EST PAS UNE COQUETTERIE DE STYLE, C'EST LE DISPOSITIF ANTI-FRAUDE DE
 * L'ACTE NOTARIÉ. Un chiffre se rature, se surcharge, gagne un zéro : « 15 000
 * 000 » devient « 150 000 000 » d'un trait de stylo. « Quinze millions » ne
 * devient rien du tout. La double écriture est redondante EXPRÈS — et le jour
 * où les deux divergent, c'est la lettre qui l'emporte, précisément parce
 * qu'elle résiste à la retouche.
 *
 * ⚠️ ET SANS ELLE, LE `default` DE `valeurClause` IMPRIMERAIT « 150000000 ».
 * Pas faux — illisible, sans devise, et indiscernable d'un numéro de dossier
 * sur un document que quelqu'un signera.
 */

const UNITES = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf',
  'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
const DIZAINES = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante',
  'soixante', 'quatre-vingt', 'quatre-vingt'];

/**
 * Un entier de 0 à 999, en français.
 *
 * ⚠️ LES TROIS PIÈGES DU FRANÇAIS, ET ILS SONT TOUS DANS CETTE FONCTION :
 *   · 71 = « soixante et onze », pas « septante-et-un » ni « soixante-onze » ;
 *   · 80 = « quatre-vingts » AVEC s, mais 81 = « quatre-vingt-un » SANS ;
 *   · 200 = « deux cents » AVEC s, mais 201 = « deux cent un » SANS.
 * Le `s` de « vingts » et « cents » ne survit que s'ils terminent le nombre.
 */
function centaineEnLettres(n: number, terminal = true): string {
  if (n < 20) return UNITES[n]!;
  if (n < 100) {
    const d = Math.floor(n / 10);
    const u = n % 10;
    // 70-79 et 90-99 se comptent en « soixante-dix » et « quatre-vingt-dix ».
    if (d === 7 || d === 9) {
      const reste = UNITES[10 + u]!;
      const liaison = d === 7 && u === 1 ? ' et ' : '-';
      return DIZAINES[d]! + liaison + reste;
    }
    // ⚠️ LE `s` DE « QUATRE-VINGTS » NE SURVIT QU'EN FIN DE NOMBRE.
    // « quatre-vingts » seul, mais « quatre-vingt mille » et « quatre-vingt-un ».
    // Défaut trouvé par l'essai, pas par relecture : la règle est connue de
    // tous et s'oublie exactement ici, parce que la fonction ne savait pas
    // encore si quelque chose la suivait.
    if (u === 0) return d === 8 && terminal ? 'quatre-vingts' : DIZAINES[d]!;
    if (u === 1 && d !== 8) return `${DIZAINES[d]!} et un`;
    return `${DIZAINES[d]!}-${UNITES[u]!}`;
  }
  const c = Math.floor(n / 100);
  const reste = n % 100;
  const tete = c === 1 ? 'cent' : `${UNITES[c]!} cent`;
  // Même règle pour « cents » : « deux cents », mais « deux cent mille ».
  if (reste === 0) return c === 1 ? 'cent' : (terminal ? `${tete}s` : tete);
  return `${tete} ${centaineEnLettres(reste, terminal)}`;
}

/** Les échelles, avec leur pluriel. « mille » est INVARIABLE, « million » non. */
const ECHELLES: { valeur: number; singulier: string; pluriel: string }[] = [
  { valeur: 1_000_000_000, singulier: 'milliard', pluriel: 'milliards' },
  { valeur: 1_000_000,     singulier: 'million',  pluriel: 'millions' },
  { valeur: 1_000,         singulier: 'mille',    pluriel: 'mille' },
];

/**
 * Un entier positif en toutes lettres.
 *
 * ⚠️ « MILLE » NE PREND JAMAIS DE S — c'est la seule échelle invariable du
 * français, et l'oubli produit « deux milles » sur un acte signé.
 * ⚠️ ET « UN MILLE » NE SE DIT PAS : 1 000 est « mille », pas « un mille ».
 * En revanche 1 000 000 est bien « un million ».
 */
export function entierEnLettres(valeur: number): string {
  if (!Number.isFinite(valeur)) return '—';
  const n = Math.trunc(Math.abs(valeur));
  if (n === 0) return 'zéro';
  const morceaux: string[] = [];
  let reste = n;
  for (const e of ECHELLES) {
    const combien = Math.floor(reste / e.valeur);
    if (combien === 0) continue;
    reste -= combien * e.valeur;
    // ⚠️ `terminal = false` : ce groupe est suivi de son échelle, donc ni
    // « quatre-vingts » ni « cents » ne gardent leur `s`.
    const tete = combien === 1 && e.valeur === 1_000 ? '' : `${centaineEnLettres(combien, false)} `;
    morceaux.push(`${tete}${combien > 1 ? e.pluriel : e.singulier}`);
  }
  if (reste > 0) morceaux.push(centaineEnLettres(reste));
  const mots = morceaux.join(' ');
  return valeur < 0 ? `moins ${mots}` : mots;
}

/**
 * Un montant en chiffres, groupé par milliers.
 *
 * ⚠️ L'ESPACE EST UNE ESPACE INSÉCABLE FINE (U+202F), PAS UN ESPACE ORDINAIRE.
 * Un espace ordinaire autorise le retour à la ligne au milieu d'un prix : « 150
 * 000 » coupé en fin de ligne se lit « 150 » suivi de « 000 » à la ligne
 * suivante, et sur un acte imprimé cette coupure est indéfendable.
 */
export function montantEnChiffres(valeur: unknown, devise = 'GNF'): string {
  if (valeur === null || valeur === undefined || valeur === '') return '—';
  const n = typeof valeur === 'number' ? valeur : Number(valeur);
  if (!Number.isFinite(n)) return '—';
  const entier = Math.trunc(Math.abs(n));
  const groupes = String(entier).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const signe = n < 0 ? '-' : '';
  return `${signe}${groupes} ${devise}`;
}

/** Le nom de la devise, décliné : « francs guinéens ». */
const DEVISE_EN_LETTRES: Record<string, string> = {
  GNF: 'francs guinéens',
  XOF: 'francs CFA',
  EUR: 'euros',
  USD: 'dollars des États-Unis',
};

/**
 * La double écriture complète, telle qu'elle s'imprime sur l'acte.
 *
 *   « 150 000 000 GNF (cent cinquante millions de francs guinéens) »
 *
 * ⚠️ UNE DEVISE INCONNUE NE SE TAIT PAS. Elle s'imprime telle quelle, en
 * capitales, plutôt que de disparaître : un montant sans devise sur un acte de
 * vente est un montant qu'on peut relire dans n'importe quelle monnaie.
 */
export function montantSurActe(valeur: unknown, devise = 'GNF'): string {
  const chiffres = montantEnChiffres(valeur, devise);
  if (chiffres === '—') return '—';
  const n = typeof valeur === 'number' ? valeur : Number(valeur);
  const nom = DEVISE_EN_LETTRES[devise] ?? devise;
  const lettres = entierEnLettres(n);
  /**
   * ⚠️ « DEUX MILLIONS DE FRANCS », MAIS « DEUX MILLE FRANCS ».
   *
   * « million » et « milliard » sont des NOMS : ils appellent la préposition.
   * « mille » est un adjectif numéral : il n'en appelle aucune. Et le « de » ne
   * se met que si le nombre SE TERMINE sur l'échelle — « deux millions cinq
   * cent mille francs » n'en prend pas, parce que « francs » suit « mille ».
   *
   * Sans cette règle, l'acte imprime « cent cinquante millions francs
   * guinéens », qui n'est pas du français et se remarque au premier lecteur.
   */
  const appelleDe = /\b(millions?|milliards?)$/.test(lettres);
  return `${chiffres} (${lettres}${appelleDe ? ' de ' : ' '}${nom})`;
}
